// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { Map as MapComponent } from 'sonata-platform-components/lib/types/types/reportingMap.types';
import { BoxV2, ToggleButtonGroup } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import {
  reportAdvancedFilterDeleteMap,
  reportAdvancedFilterSetMaps
} from '@/store/actions/reportingAdvancedFilter.actions';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { MainSettingsPanelStyle } from './mainSettingsPanel.style';

export function MainSettingsPanel() {
  const dispatch = useDispatch();
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const {
    addMap,
    deleteMap,
    getNextMapId,
    setMapMode,
    state: { maps }
  } = useReportingMap();

  const mode = useMemo(() => maps[0]?.mode || 'dark', [maps]);
  const numberOfMaps = useMemo(() => `${maps.length}`, [maps]);

  const { formatMessage } = useIntl();
  const numberOfMapsOptions = useMemo(
    () => [
      {
        id: '1',
        text: formatMessage({
          defaultMessage: 'One map',
          id: 'reporting.map.controlPanel.mainSettings.oneMap'
        })
      },
      {
        id: '2',
        text: formatMessage({
          defaultMessage: 'Double map',
          id: 'reporting.map.controlPanel.mainSettings.doubleMap'
        })
      }
    ],
    []
  );
  const themeModeOptions = useMemo(
    () => [
      {
        id: 'dark',
        text: formatMessage({
          defaultMessage: 'Dark mode',
          id: 'reporting.map.controlPanel.mainSettings.darkMode'
        })
      },
      {
        id: 'light',
        text: formatMessage({
          defaultMessage: 'Light mode',
          id: 'reporting.map.controlPanel.mainSettings.lightMode'
        })
      }
    ],
    []
  );

  const onChangeNumberOfMaps = useCallback(
    id => {
      const amount = parseInt(id);
      if (maps.length > amount) {
        deleteMap({ id: maps[maps.length - 1].id });
        dispatch(reportAdvancedFilterDeleteMap('maps', maps[maps.length - 1].id));
      } else if (maps.length < amount) {
        addMap();
        dispatch(reportAdvancedFilterSetMaps('maps', getNextMapId(maps)));
      }
    },
    [maps]
  );

  const onChangeMode = useCallback(
    value => {
      maps.forEach(({ id }: { id: MapComponent['id'] }) => {
        setMapMode({
          id,
          value
        });
      });
    },
    [maps]
  );

  return (
    <MainSettingsPanelStyle className="mainSettingsPanel">
      <div className="mainSettingsPanel__section">
        <BoxV2 className="mainSettingsPanel__section__box" id="mainSettingsPanel-section-box">
          <BoxV2.Header bgColor="background01" padding="16px 16px 0 16px">
            <BoxV2.Header.Title
              title={formatMessage({
                defaultMessage: 'Map view',
                id: 'reporting.map.controlPanel.mainSettings.mapView'
              })}
              titleFontStyles="bodyDefault fontSemiBold"
            />
          </BoxV2.Header>
          <BoxV2.Body bgColor="background01" padding="16px 16px 24px 16px">
            <div className="mainSettingsPanel__section__content">
              {checkUserResourcePermissions('reportingDoubleMapSettings', user, userRoles) && (
                <ToggleButtonGroup
                  id="mainSettingsPanel-toggleButtonGroup-numberMapSelector"
                  items={numberOfMapsOptions}
                  onChangeItemSelected={onChangeNumberOfMaps}
                  value={numberOfMaps}
                />
              )}
              <ToggleButtonGroup
                id="mainSettingsPanel-toggleButtonGroup-mapMode"
                items={themeModeOptions}
                onChangeItemSelected={onChangeMode}
                value={mode}
              />
            </div>
          </BoxV2.Body>
        </BoxV2>
      </div>
    </MainSettingsPanelStyle>
  );
}
